import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import './theme.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ForcePasswordChange from './components/ForcePasswordChange';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import UserDetail from './components/UserDetail';
import Settings from './components/Settings';
import TimeEntries from './components/TimeEntries';
import UserManagement from './components/UserManagement';
import TelegramMessages from './components/TelegramMessages';
import ReviewTimes from './components/ReviewTimes';
import MonthlyReviews from './components/MonthlyReviews';
import Layout from './components/Layout';

// Protected Route Component - any authenticated account (admin or manager)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return children;
};

// Admin-only route - managers get bounced to their own landing page.
// Mirrors the backend's requireRole('admin') checks; this is UX, not the
// security boundary.
const AdminRoute = ({ children }) => (
  <ProtectedRoute>
    <RequireAdmin>{children}</RequireAdmin>
  </ProtectedRoute>
);

const RequireAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role !== 'admin') {
    return <Navigate to="/working-time/review" />;
  }
  return children;
};

// Public Route Component (redirect away if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return children;
  return <Navigate to={user?.role === 'admin' ? '/dashboard' : '/working-time/review'} />;
};

// Role-aware landing page for "/"
const DefaultRedirect = () => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return <Navigate to={user?.role === 'admin' ? '/dashboard' : '/working-time/review'} />;
};

function App() {
  return (
    <AppProvider
      i18n={{
        Polaris: {
          Avatar: {
            label: 'Avatar',
            labelWithInitials: 'Avatar with initials {initials}',
          },
          ContextualSaveBar: {
            save: 'Save',
            discard: 'Discard',
          },
          TextField: {
            characterCount: '{count} characters',
          },
          TopBar: {
            toggleMenuLabel: 'Toggle menu',
            SearchField: {
              clearButtonLabel: 'Clear',
              search: 'Search',
            },
          },
          Modal: {
            iFrameTitle: 'body markup',
          },
          Frame: {
            skipToContent: 'Skip to content',
            navigationLabel: 'Navigation',
            Navigation: {
              closeMobileNavigationLabel: 'Close navigation',
            },
          },
        },
      }}
    >
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AdminRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <AdminRoute>
                  <Layout>
                    <UserDetail />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/time-entries"
              element={
                <AdminRoute>
                  <Layout>
                    <TimeEntries />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <AdminRoute>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/working-time/telegram"
              element={
                <AdminRoute>
                  <Layout>
                    <TelegramMessages />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/monthly-reviews"
              element={
                <AdminRoute>
                  <Layout>
                    <MonthlyReviews />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/working-time/review"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReviewTimes />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
        </Router>
      </AuthProvider>
    </AppProvider>
  );
}

export default App;